import styled from '@emotion/styled'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { extractDbIdFromGraphqlId } from '../../../../util/string'
import type { OrganizationMembership } from './OrganizationsSection'
import OrganizationDetail from './OrganizationDetail'
import { Hugeicon } from '../../../../icons/Hugeicon'

type OrganizationRowProps = {
  membership: OrganizationMembership
  currentUserId?: string
  defaultExpanded?: boolean
  onDeleteSuccess?: () => void
}

const OrganizationRow: React.FC<OrganizationRowProps> = ({
  membership,
  currentUserId,
  defaultExpanded = false,
  onDeleteSuccess,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const { organization, role } = membership
  const isOwner = role === 'owner'
  const hasCalendar = organization.hasActiveSubscription

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  const orgDbId = extractDbIdFromGraphqlId(organization.id)

  return (
    <CardContainer isExpanded={isExpanded}>
      <CardHeader onClick={handleToggle}>
        <CardLeft>
          <OrgAvatar hasLogo={!!organization.logoPublicId}>
            {organization.logoPublicId ? (
              <img
                src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/w_80,h_80,c_fill/${organization.logoPublicId}`}
                alt={organization.name}
              />
            ) : (
              <BuildingIcon />
            )}
          </OrgAvatar>
          <OrgInfo>
            <OrgNameRow>
              <OrgName>{organization.name}</OrgName>
              {hasCalendar && (
                <CalendarBadge>
                  <CalendarCheckIcon />
                  Calendar
                </CalendarBadge>
              )}
            </OrgNameRow>
            <OrgMeta>
              <RoleBadge isOwner={isOwner}>{isOwner ? 'Owner' : 'Member'}</RoleBadge>
              <MetaDot />
              <MemberCount>{organization.memberCount} {organization.memberCount === 1 ? 'member' : 'members'}</MemberCount>
            </OrgMeta>
          </OrgInfo>
        </CardLeft>

        <CardRight>
          {isOwner && !hasCalendar && (
            <GetCalendarLink
              to={`/dash/calendar-for-businesses/signup`}
              onClick={e => e.stopPropagation()}
            >
              Get Calendar
            </GetCalendarLink>
          )}
          {isOwner && hasCalendar && (
            <ManageCalendarLink
              to={`/dash/business-calendar/${orgDbId}`}
              onClick={e => e.stopPropagation()}
            >
              Manage Calendar
            </ManageCalendarLink>
          )}
          <ExpandButton isExpanded={isExpanded}>
            <ChevronIcon />
          </ExpandButton>
        </CardRight>
      </CardHeader>

      {isExpanded && (
        <OrganizationDetail
          organization={organization}
          currentUserId={currentUserId}
          membershipId={membership.id}
          isOwner={isOwner}
          onDeleteSuccess={onDeleteSuccess}
        />
      )}
    </CardContainer>
  )
}

export default OrganizationRow

const BuildingIcon: React.FC = () => <Hugeicon name="bank" size={20} />

const ChevronIcon: React.FC = () => <Hugeicon name="arrow-down" size={18} />

const CalendarCheckIcon: React.FC = () => <Hugeicon name="calendar-check" size={12} />

const CardContainer = styled.div<{ isExpanded: boolean }>`
  background: white;
  border-radius: 12px;
  border: 1px solid ${props => props.isExpanded ? '#3b82f6' : '#e2e8f0'};
  box-shadow: ${props => props.isExpanded
    ? '0 4px 12px rgba(59, 130, 246, 0.1)'
    : '0 1px 3px rgba(0, 0, 0, 0.04)'};
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.isExpanded ? '#3b82f6' : '#cbd5e1'};
    box-shadow: ${props => props.isExpanded
      ? '0 4px 12px rgba(59, 130, 246, 0.1)'
      : '0 2px 8px rgba(0, 0, 0, 0.06)'};
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  gap: 16px;
`

const CardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
`

const OrgAvatar = styled.div<{ hasLogo: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.hasLogo ? 'white' : 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  overflow: hidden;
  border: ${props => props.hasLogo ? '1px solid #e2e8f0' : 'none'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const OrgInfo = styled.div`
  min-width: 0;
  flex: 1;
`

const OrgNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const OrgName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CalendarBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
  color: #3b82f6;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  white-space: nowrap;
`

const OrgMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`

const RoleBadge = styled.span<{ isOwner: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.isOwner ? '#0d9488' : '#8b5cf6'};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #cbd5e1;
`

const MemberCount = styled.span`
  font-size: 13px;
  color: #64748b;
`

const CardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`

const GetCalendarLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  text-decoration: none;
  transition: opacity 0.15s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
    color: white;
  }

  @media (max-width: 600px) {
    display: none;
  }
`

const ManageCalendarLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  background: white;
  color: #3b82f6;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
  text-decoration: none;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #2563eb;
  }

  @media (max-width: 600px) {
    display: none;
  }
`

const ExpandButton = styled.div<{ isExpanded: boolean }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: all 0.2s ease;
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  border-radius: 6px;

  &:hover {
    color: #64748b;
    background: #f1f5f9;
  }
`
